import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createCampaign,
  fetchCampaigns,
  publishCampaign,
  unpublishCampaign,
  updateCampaign,
} from '../../api/campaigns';
import { fetchProducts } from '../../api/products';
import { fetchServices } from '../../api/services';
import DashboardShell from '../../components/ui/DashboardShell';
import { useToast } from '../../context/ToastContext';

const emptyDraft = () => ({
  name: '',
  bannerText: '',
  description: '',
  theme: 'TEAL',
  isHighlighted: true,
  scope: 'SERVICES',
  discountMode: 'PERCENTAGE',
  discountValue: 10,
  audience: 'ALL_CLIENTS',
  clientIds: [],
  maxRedemptions: null,
  endAt: '',
  serviceIds: [],
  productIds: [],
});

const themeIds = ['TEAL', 'PURPLE', 'CORAL', 'GLOW'];

const pill = (status) => {
  if (status === 'PUBLISHED') return 'bg-emerald-600/20 text-emerald-300';
  if (status === 'DRAFT') return 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400';
  if (status === 'ENDED') return 'bg-red-600/20 text-red-300';
  return 'bg-amber-600/20 text-amber-300';
};

const CheckboxList = ({ title, items, selectedIds, onChange, emptyHint, noItemsLabel }) => (
  <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 p-3">
    <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{title}</p>
    <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-500">
      {emptyHint}
    </p>
    <div className="mt-3 max-h-48 space-y-2 overflow-y-auto pr-1">
      {items.map((it) => {
        const checked = selectedIds.includes(it._id);
        return (
          <label key={it._id} className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => {
                if (e.target.checked) onChange([...selectedIds, it._id]);
                else onChange(selectedIds.filter((id) => id !== it._id));
              }}
            />
            <span className="truncate">{it.name}</span>
          </label>
        );
      })}
      {!items.length && (
        <p className="text-xs text-zinc-600 dark:text-zinc-500">{noItemsLabel}</p>
      )}
    </div>
  </div>
);

const CampaignPreview = ({ draft, t }) => {
  const color =
    draft.theme === 'PURPLE'
      ? 'from-purple-600/30 to-fuchsia-600/20'
      : draft.theme === 'CORAL'
        ? 'from-rose-600/30 to-amber-600/20'
        : draft.theme === 'GLOW'
          ? 'from-glow-600/30 to-cyan-600/20'
          : 'from-teal-600/30 to-emerald-600/20';

  const discount =
    draft.discountMode === 'PERCENTAGE'
      ? t('marketplace.percentOff', { value: draft.discountValue })
      : t('marketplace.flatOff', { value: Number(draft.discountValue || 0).toLocaleString() });

  return (
    <div className="w-full overflow-hidden rounded-2xl border border-zinc-200 dark:border-zinc-800">
      <div className={`w-full bg-gradient-to-r ${color} px-6 py-8 sm:px-10 sm:py-10`}>
        <p className="text-xs font-medium uppercase tracking-wide text-zinc-700 dark:text-zinc-200/80">
          {t('campaigns.previewLabel')}
        </p>
        <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            {draft.isHighlighted && (
              <p className="text-xs text-zinc-700 dark:text-zinc-200/80">{t('marketplace.limitedOffer')}</p>
            )}
            <p className="mt-1 text-2xl font-semibold sm:text-3xl">
              {draft.bannerText || draft.name || t('campaigns.previewBanner')}
            </p>
            <p className="mt-2 max-w-3xl text-sm text-zinc-700 dark:text-zinc-200/85 sm:text-base">
              {draft.description || t('campaigns.previewMessage')}
            </p>
            {draft.endAt && (
              <p className="mt-3 text-xs text-zinc-700 dark:text-zinc-200/70">
                {t('marketplace.ends', { date: new Date(draft.endAt).toLocaleString() })}
              </p>
            )}
          </div>
          <span className="shrink-0 rounded-full bg-black/10 px-5 py-2.5 text-sm font-semibold text-zinc-900 dark:bg-white/10 dark:text-zinc-100 sm:text-base">
            {discount}
          </span>
        </div>
      </div>
    </div>
  );
};

const SalonCampaignsPage = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);

  const { data } = useQuery({
    queryKey: ['campaigns'],
    queryFn: fetchCampaigns,
  });

  const { data: servicesData } = useQuery({
    queryKey: ['services', 'campaigns-picker'],
    queryFn: () => fetchServices({ limit: 200, page: 1 }),
    staleTime: 60_000,
  });

  const { data: productsData } = useQuery({
    queryKey: ['products', 'campaigns-picker'],
    queryFn: () => fetchProducts({ limit: 200, page: 1 }),
    staleTime: 60_000,
  });

  const campaigns = data?.campaigns || [];
  const services = servicesData?.services || [];
  const products = productsData?.products || [];

  const savingPayload = useMemo(() => {
    const endAt = draft.endAt ? new Date(draft.endAt) : null;
    return {
      name: draft.name.trim(),
      bannerText: draft.bannerText.trim(),
      description: draft.description.trim(),
      theme: draft.theme,
      isHighlighted: !!draft.isHighlighted,
      scope: draft.scope,
      discountMode: draft.discountMode,
      discountValue: Number(draft.discountValue || 0),
      audience: draft.audience,
      clientIds: draft.clientIds || [],
      maxRedemptions: draft.audience === 'LIMITED_COUNT' ? Number(draft.maxRedemptions || 0) : null,
      endAt: endAt ? endAt.toISOString() : null,
      serviceIds: draft.scope === 'SERVICES' || draft.scope === 'BOTH' ? draft.serviceIds : [],
      productIds: draft.scope === 'PRODUCTS' || draft.scope === 'BOTH' ? draft.productIds : [],
    };
  }, [draft]);

  const reset = () => {
    setEditing(null);
    setDraft(emptyDraft());
  };

  const saveMut = useMutation({
    mutationFn: () =>
      editing ? updateCampaign(editing._id, savingPayload) : createCampaign(savingPayload),
    onSuccess: () => {
      showToast(editing ? t('toast.campaignUpdated') : t('toast.campaignCreated'), 'success');
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      reset();
    },
    onError: (err) => showToast(err.response?.data?.message || t('toast.errorGeneric'), 'error'),
  });

  const publishMut = useMutation({
    mutationFn: (id) => publishCampaign(id),
    onSuccess: () => {
      showToast(t('toast.campaignPublished'), 'success');
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
    onError: (err) => showToast(err.response?.data?.message || t('toast.errorGeneric'), 'error'),
  });

  const unpublishMut = useMutation({
    mutationFn: (id) => unpublishCampaign(id),
    onSuccess: () => {
      showToast(t('toast.campaignUnpublished'), 'success');
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
    onError: (err) => showToast(err.response?.data?.message || t('toast.errorGeneric'), 'error'),
  });

  const startEdit = (c) => {
    setEditing(c);
    setDraft({
      name: c.name || '',
      bannerText: c.bannerText || '',
      description: c.description || '',
      theme: c.theme || 'TEAL',
      isHighlighted: c.isHighlighted !== false,
      scope: c.scope || 'SERVICES',
      discountMode: c.discountMode || 'PERCENTAGE',
      discountValue: Number(c.discountValue || 0),
      audience: c.audience || 'ALL_CLIENTS',
      clientIds: (c.clientIds || []).map(String),
      maxRedemptions: c.maxRedemptions ?? null,
      endAt: c.endAt ? new Date(c.endAt).toISOString().slice(0, 16) : '',
      serviceIds: (c.serviceIds || []).map(String),
      productIds: (c.productIds || []).map(String),
    });
  };

  const canEdit = !editing || !['PUBLISHED', 'ENDED'].includes(editing.status);

  return (
    <DashboardShell
      title={t('campaigns.title')}
      subtitle={t('campaigns.pageSubtitle')}
    >
      <CampaignPreview draft={draft} t={t} />

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold">{editing ? t('campaigns.editCampaign') : t('campaigns.createCampaign')}</p>
                <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-500">
                  {t('campaigns.draftHint')}
                </p>
              </div>
              {editing && (
                <button type="button" onClick={reset} className="text-xs text-zinc-600 dark:text-zinc-400 hover:underline">
                  {t('campaigns.newDraft')}
                </button>
              )}
            </div>

            {!canEdit && (
              <p className="mt-3 rounded-lg border border-amber-800/40 bg-amber-950/20 p-3 text-sm text-amber-200">
                {t('campaigns.publishedLocked')}
              </p>
            )}

            <div className="mt-4 grid gap-3">
              <label className="text-xs text-zinc-600 dark:text-zinc-400">
                {t('campaigns.nameRequired')}
                <input
                  disabled={!canEdit}
                  value={draft.name}
                  onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                  className="mt-1 w-full rounded border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-2 py-2 text-sm"
                />
              </label>

              <label className="text-xs text-zinc-600 dark:text-zinc-400">
                {t('campaigns.bannerShort')}
                <input
                  disabled={!canEdit}
                  value={draft.bannerText}
                  onChange={(e) => setDraft((d) => ({ ...d, bannerText: e.target.value }))}
                  placeholder={t('campaigns.bannerPlaceholder')}
                  className="mt-1 w-full rounded border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-2 py-2 text-sm"
                />
              </label>

              <label className="text-xs text-zinc-600 dark:text-zinc-400">
                {t('campaigns.descriptionOptional')}
                <textarea
                  disabled={!canEdit}
                  value={draft.description}
                  onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value.slice(0, 280) }))}
                  rows={3}
                  className="mt-1 w-full rounded border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-2 py-2 text-sm"
                />
              </label>

              <div className="grid gap-3 sm:grid-cols-2">
                <label className="text-xs text-zinc-600 dark:text-zinc-400">
                  {t('campaigns.scope')}
                  <select
                    disabled={!canEdit}
                    value={draft.scope}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        scope: e.target.value,
                        serviceIds: e.target.value === 'PRODUCTS' ? [] : d.serviceIds,
                        productIds: e.target.value === 'SERVICES' ? [] : d.productIds,
                      }))
                    }
                    className="mt-1 w-full rounded border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-2 py-2 text-sm"
                  >
                    <option value="SERVICES">{t('campaigns.scopeServices')}</option>
                    <option value="PRODUCTS">{t('campaigns.scopeProducts')}</option>
                    <option value="BOTH">{t('campaigns.scopeBoth')}</option>
                  </select>
                </label>

                <label className="text-xs text-zinc-600 dark:text-zinc-400">
                  {t('campaigns.theme')}
                  <select
                    disabled={!canEdit}
                    value={draft.theme}
                    onChange={(e) => setDraft((d) => ({ ...d, theme: e.target.value }))}
                    className="mt-1 w-full rounded border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-2 py-2 text-sm"
                  >
                    {themeIds.map((id) => (
                      <option key={id} value={id}>{t(`campaigns.theme${id.charAt(0)}${id.slice(1).toLowerCase()}`)}</option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <label className="text-xs text-zinc-600 dark:text-zinc-400">
                  {t('campaigns.discountMode')}
                  <select
                    disabled={!canEdit}
                    value={draft.discountMode}
                    onChange={(e) => setDraft((d) => ({ ...d, discountMode: e.target.value }))}
                    className="mt-1 w-full rounded border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-2 py-2 text-sm"
                  >
                    <option value="PERCENTAGE">{t('campaigns.percentage')}</option>
                    <option value="FLAT">{t('campaigns.flatAmount')}</option>
                  </select>
                </label>
                <label className="text-xs text-zinc-600 dark:text-zinc-400">
                  {t('campaigns.discountValue')} *
                  <input
                    disabled={!canEdit}
                    type="number"
                    min="1"
                    step="1"
                    value={draft.discountValue}
                    onChange={(e) => setDraft((d) => ({ ...d, discountValue: e.target.value }))}
                    className="mt-1 w-full rounded border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-2 py-2 text-sm"
                  />
                </label>
                <label className="text-xs text-zinc-600 dark:text-zinc-400">
                  {t('campaigns.endAtOptional')}
                  <input
                    disabled={!canEdit}
                    type="datetime-local"
                    value={draft.endAt}
                    onChange={(e) => setDraft((d) => ({ ...d, endAt: e.target.value }))}
                    className="mt-1 w-full rounded border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-2 py-2 text-sm"
                  />
                </label>
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input
                  disabled={!canEdit}
                  type="checkbox"
                  checked={draft.isHighlighted}
                  onChange={(e) => setDraft((d) => ({ ...d, isHighlighted: e.target.checked }))}
                />
                <span className="text-zinc-700 dark:text-zinc-300">{t('campaigns.highlightCheckbox')}</span>
              </label>

              {(draft.scope === 'SERVICES' || draft.scope === 'BOTH') && (
                <CheckboxList
                  title={t('campaigns.appliesToServices')}
                  items={services}
                  selectedIds={draft.serviceIds}
                  onChange={(ids) => setDraft((d) => ({ ...d, serviceIds: ids }))}
                  emptyHint={t('campaigns.leaveEmptyAll')}
                  noItemsLabel={t('campaigns.noItemsFound')}
                />
              )}
              {(draft.scope === 'PRODUCTS' || draft.scope === 'BOTH') && (
                <CheckboxList
                  title={t('campaigns.appliesToProducts')}
                  items={products}
                  selectedIds={draft.productIds}
                  onChange={(ids) => setDraft((d) => ({ ...d, productIds: ids }))}
                  emptyHint={t('campaigns.leaveEmptyAll')}
                  noItemsLabel={t('campaigns.noItemsFound')}
                />
              )}

              <button
                type="button"
                disabled={!canEdit || saveMut.isPending || !draft.name.trim()}
                onClick={() => saveMut.mutate()}
                className="w-full rounded-lg bg-glow-600 py-2.5 text-sm font-medium text-white disabled:opacity-50"
              >
                {saveMut.isPending ? t('common.saving') : editing ? t('common.saveChanges') : t('campaigns.saveDraft')}
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-5">
            <p className="text-sm font-semibold">{t('campaigns.yourCampaigns')}</p>
            <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-500">
              {t('campaigns.publishWhenReady')}
            </p>
          </div>

          <div className="space-y-3">
            {campaigns.map((c) => (
              <div key={c._id} className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium">{c.name}</p>
                      <span className={`rounded-full px-2 py-0.5 text-xs ${pill(c.status)}`}>{t(`status.campaign.${c.status}`)}</span>
                    </div>
                    <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-500">
                      {c.bannerText || c.description || '—'}
                    </p>
                    <p className="mt-2 text-xs text-zinc-600 dark:text-zinc-500">
                      {t('campaigns.endsLabel', { date: c.endAt ? new Date(c.endAt).toLocaleString() : t('common.noEndDate') })}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => startEdit(c)}
                    className="rounded-lg border border-zinc-300 dark:border-zinc-700 px-3 py-1.5 text-xs hover:bg-zinc-100 dark:hover:bg-zinc-800"
                  >
                    {t('common.edit')}
                  </button>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {c.status === 'PUBLISHED' ? (
                    <button
                      type="button"
                      disabled={unpublishMut.isPending}
                      onClick={() => unpublishMut.mutate(c._id)}
                      className="rounded-lg border border-amber-700/60 px-3 py-1.5 text-xs text-amber-400 hover:bg-amber-950/20 disabled:opacity-50"
                    >
                      {unpublishMut.isPending ? t('campaigns.unpublishing') : t('campaigns.unpublish')}
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={publishMut.isPending}
                      onClick={() => publishMut.mutate(c._id)}
                      className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs text-white hover:bg-emerald-500 disabled:opacity-50"
                    >
                      {publishMut.isPending ? t('campaigns.publishing') : t('campaigns.publish')}
                    </button>
                  )}
                </div>
              </div>
            ))}

            {!campaigns.length && (
              <div className="rounded-xl border border-dashed border-zinc-300 dark:border-zinc-700 p-8 text-center text-sm text-zinc-600 dark:text-zinc-500">
                {t('campaigns.noCampaignsDraft')}
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardShell>
  );
};

export default SalonCampaignsPage;

